import { z } from "zod";

/**
 * Leaseable-resource pausing and the operator "blocker" that reports it.
 *
 * Shared because three consumers must agree on one shape: the backend writes the pause block onto
 * `LeaseableResource.metadata` and sets the `blocker` on its operator tool outputs, the agent loop
 * reads the blocker to stop retrying a login that cannot succeed, and the settings UI reads the
 * pause block to explain why a resource is paused (and, later, to resume it).
 */

/**
 * `LeaseableResource.resourceType` for a resource that wraps a stored Credential. It is the only
 * type the settings UI offers. Rejection handling records these resources without deactivating them.
 */
export const LEASEABLE_RESOURCE_TYPE_CREDENTIAL = "credential";

/**
 * Where a credential was presented when it was rejected. Mirrors the A2A wire vocabulary
 * (`CredentialRejectedError.surface`): `portal` is the Azure/Entra sign-in page driven by the
 * pre-agent AVD login, `vm` the in-session RDP password prompt, `application` an application
 * inside the desktop reached through the operator's `fill_credential` tool.
 */
export const CREDENTIAL_REJECTION_SURFACES = [
  "portal",
  "vm",
  "application",
] as const;

export const credentialRejectionSurfaceSchema = z.enum(
  CREDENTIAL_REJECTION_SURFACES,
);

export type CredentialRejectionSurface = z.infer<
  typeof credentialRejectionSurfaceSchema
>;

/**
 * The reason recorded by the former automatic credential-rejection pause. Existing pause blocks
 * remain readable so an administrator can resume resources paused before automatic pausing was
 * removed. Manual deactivation carries no pause block.
 */
export const LEASEABLE_RESOURCE_PAUSE_REASON_CREDENTIAL_REJECTED =
  "credential_rejected";

/** Key the pause block lives under inside `LeaseableResource.metadata`. */
export const LEASEABLE_RESOURCE_PAUSE_METADATA_KEY = "pause";

/**
 * Why a leaseable resource was paused automatically, merged into (never replacing) the resource's
 * existing metadata.
 *
 * Ids only — no credential value and no operator prose. The wire's `message` is deliberately left
 * out: it is redacted upstream, but a redactor is a filter and a database row is forever.
 *
 * Ids are plain strings rather than `z.uuid()` on purpose: this schema's job on the read side is
 * to explain an existing pause, and a stored block must never be dropped (leaving a resource
 * paused with no visible reason) because an id failed a format check.
 */
export const leaseableResourcePauseSchema = z.object({
  reason: z.literal(LEASEABLE_RESOURCE_PAUSE_REASON_CREDENTIAL_REJECTED),
  /** The rejected credential, or null when the operator could not resolve one. */
  credentialId: z.string().nullable(),
  /** Non-secret label of the rejected credential. */
  credentialName: z.string(),
  surface: credentialRejectionSurfaceSchema,
  /** ISO-8601 instant the rejection was acted on. */
  rejectedAt: z.iso.datetime(),
  /** The run that HELD the lease — a task-loop child's leases belong to its parent loop run. */
  agentRunId: z.string(),
  /** The run that actually hit the rejection; equals `agentRunId` outside the task loop. */
  sourceAgentRunId: z.string(),
});

export type LeaseableResourcePause = z.infer<
  typeof leaseableResourcePauseSchema
>;

/**
 * Read the pause block off a resource's metadata. Null when the resource was never auto-paused
 * (including an administrator's manual deactivation) or the stored block does not parse.
 */
export function readLeaseableResourcePause(
  metadata: Record<string, unknown> | null | undefined,
): LeaseableResourcePause | null {
  const raw = metadata?.[LEASEABLE_RESOURCE_PAUSE_METADATA_KEY];
  if (raw == null) return null;
  const parsed = leaseableResourcePauseSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** Key the rejection stamp lives under inside `Credential.metadata`. */
export const CREDENTIAL_LAST_REJECTED_METADATA_KEY = "lastRejected";

/**
 * The rejection stamp merged into `Credential.metadata`. Ids and labels only — never the secret.
 *
 * It exists next to the pause block rather than instead of it: the pause explains why one resource
 * refuses to lease, this explains why a credential keeps failing even when no resource wraps it
 * (nothing to pause), and the settings UI reads it without joining through the resources.
 *
 * Ids are plain strings for the same reason the pause block's are — a stored stamp must not be
 * dropped on the read side because an id failed a format check.
 */
export const credentialRejectionStampSchema = z.object({
  /** ISO-8601 instant of the most recent rejection. */
  at: z.iso.datetime(),
  /** The run that hit the rejection. */
  agentRunId: z.string(),
  surface: credentialRejectionSurfaceSchema,
  /** Leaseable resources wrapping the credential; empty when none wrap it. */
  resourceIds: z.array(z.string()),
});

export type CredentialRejectionStamp = z.infer<
  typeof credentialRejectionStampSchema
>;

/**
 * Read the rejection stamp off a credential's metadata. Null when the credential was never
 * reported rejected, or the stored stamp does not parse.
 */
export function readCredentialRejectionStamp(
  metadata: Record<string, unknown> | null | undefined,
): CredentialRejectionStamp | null {
  const raw = metadata?.[CREDENTIAL_LAST_REJECTED_METADATA_KEY];
  if (raw == null) return null;
  const parsed = credentialRejectionStampSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * A terminal, non-retryable obstacle an operator tool ran into, reported to the calling LLM
 * alongside the ordinary `error` string.
 *
 * It exists because the model cannot tell "this login needs one more try" from "this login can
 * never succeed until a human replaces the password" out of prose, and the second is expensive to
 * retry (each attempt provisions a cloud browser and spends minutes on a portal). A blocker means
 * the stored credential was rejected and this run must stop. The resource remains available
 * to later runs.
 */
/**
 * Whether the resource was paused. The credential policy controls automatic pausing. A rejection still stops the current run.
 *
 * - `not_paused`: automatic pausing is off or its threshold has not been reached.
 * - `paused`: every leaseable resource wrapping the credential is now inactive.
 * - `nothing_to_pause`: no leaseable resource wraps the credential (or its id is unknown), so
 *   nothing stops the next run from trying the same credential.
 * - `failed`: the pause could not be written; the resource is still active.
 */
export const operatorBlockerPauseOutcomeSchema = z.enum([
  "not_paused",
  "paused",
  "nothing_to_pause",
  "failed",
]);

export type OperatorBlockerPauseOutcome = z.infer<
  typeof operatorBlockerPauseOutcomeSchema
>;

export const operatorBlockerSchema = z.object({
  kind: z.literal(LEASEABLE_RESOURCE_PAUSE_REASON_CREDENTIAL_REJECTED),
  credentialId: z.string().nullable(),
  /** Non-secret label of the rejected credential — safe to repeat to a human. */
  credentialName: z.string(),
  surface: credentialRejectionSurfaceSchema,
  pauseOutcome: operatorBlockerPauseOutcomeSchema,
});

export type OperatorBlocker = z.infer<typeof operatorBlockerSchema>;

/** Shared `.describe()` text for the `blocker` field on every operator tool's output schema. */
export const OPERATOR_BLOCKER_FIELD_DESCRIPTION =
  "Set when the failure is a rejected stored credential. End this task and ask for human input, naming the credential, without retrying the login or task in this run. `pauseOutcome` is `paused` only when the credential's configured rejection threshold was reached and its resources were paused. `not_paused` means automatic pausing is off or below the threshold, so later runs may still use the resource. `failed` means rejection handling could not be persisted. Do not claim the resource was paused unless the outcome is `paused`.";
